/**
 * Delivery Options web component
 *
 * Reads the user's selected delivery option (radio buttons inside the component),
 * fetches live prices for the fee variants, and intercepts the parent
 * <product-form> submit to add both the main product AND the delivery fee
 * variant in a single /cart/add.js request.
 *
 * Conventions:
 *  - Each radio input has `data-variant-id` (empty/blank/0 = no fee).
 *  - Each radio input has `name="properties[Delivery Option]"` so the
 *    selected label is recorded as a line item property on the main product.
 *  - The fee line item gets a property `_parent_product_id` so it can be
 *    matched back to its parent in cart/order views.
 */

if (!customElements.get('delivery-options')) {
  customElements.define(
    'delivery-options',
    class DeliveryOptions extends HTMLElement {
      constructor() {
        super();
        this.priceCache = new Map(); // variantId -> price (cents)
        this.priceFormatter = null;
      }

      connectedCallback() {
        this.radios = Array.from(this.querySelectorAll('input.delivery-options__radio'));
        if (this.radios.length === 0) return;

        this.productForm = this.closest('product-info')?.querySelector('product-form')
          || this.closest('product-form')
          || document.querySelector(`product-form[data-section="${this.dataset.sectionId}"]`)
          || document.querySelector('product-form');

        this.fetchAllPrices();
        this.bindEvents();
        this.bindPopupEvents();
        this.hookIntoProductForm();
      }

      bindEvents() {
        this.radios.forEach(radio => {
          radio.addEventListener('change', () => this.onSelectionChange());
        });
      }

      bindPopupEvents() {
        // Each info button lives inside a <label>. Clicking the label normally
        // toggles the radio. We need the info button to open a modal WITHOUT
        // selecting the option, so we stop propagation on the button click.
        // The actual modal show/hide is delegated to the theme's
        // <modal-dialog> custom element (defined in global.js), which handles
        // ESC, focus trap, body scroll lock, and click-outside-to-close.
        this.querySelectorAll('[data-info-trigger]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const popupId = btn.getAttribute('data-info-trigger');
            this.openPopup(popupId, btn);
          });
        });
      }

      openPopup(popupId, triggerEl) {
        const modal = document.getElementById(popupId);
        if (!modal) return;

        // Use the theme's <modal-dialog>.show(opener) API. It moves the modal
        // to <body>, sets [open], traps focus, locks body scroll, and listens
        // for ESC + outside-click to close. Our work here is done after this.
        if (typeof modal.show === 'function') {
          modal.show(triggerEl);
          return;
        }

        // Fallback (shouldn't happen on a normal product page, but defensive
        // in case the theme JS hasn't defined modal-dialog yet).
        modal.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
      }

      onSelectionChange() {
        // Mirror the selection into the hidden input inside the form so the
        // property submits with the main product.
        this.updateSyncedInput();

        this.dispatchEvent(new CustomEvent('delivery-option:change', {
          bubbles: true,
          detail: this.getSelected(),
        }));
      }

      getSelected() {
        const radio = this.radios.find(r => r.checked);
        if (!radio) return null;
        const rawId = radio.dataset.variantId;
        const variantId = rawId && rawId !== '' && rawId !== '0' ? parseInt(rawId, 10) : null;
        return {
          variantId: Number.isFinite(variantId) ? variantId : null,
          label: radio.dataset.label || radio.value,
        };
      }

      async fetchAllPrices() {
        // Only fetch prices for radios that don't already have a server-side
        // price (data-price or data-price-override). Skip everything else.
        const variantIds = [...new Set(
          this.radios
            .filter(r => {
              const hasServerPrice =
                (r.dataset.price && r.dataset.price !== '' && r.dataset.price !== 'null') ||
                (r.dataset.priceOverride && r.dataset.priceOverride !== '' && r.dataset.priceOverride !== 'null');
              return !hasServerPrice;
            })
            .map(r => r.dataset.variantId)
            .filter(id => id && id !== '' && id !== '0')
            .map(id => parseInt(id, 10))
            .filter(Number.isFinite)
        )];

        if (variantIds.length === 0) return; // Everything came pre-rendered.

        await Promise.all(variantIds.map(id => this.fetchVariantPrice(id)));
        this.renderPrices();
      }

      async fetchVariantPrice(variantId) {
        if (this.priceCache.has(variantId)) return this.priceCache.get(variantId);
        try {
          const res = await fetch(`/variants/${variantId}.js`, {
            headers: { 'Accept': 'application/json' }
          });
          if (!res.ok) {
            this.priceCache.set(variantId, null);
            return null;
          }
          const data = await res.json();
          this.priceCache.set(variantId, data.price); // in cents
          return data.price;
        } catch (err) {
          console.warn('[delivery-options] price fetch failed for', variantId, err);
          this.priceCache.set(variantId, null);
          return null;
        }
      }

      renderPrices() {
        this.radios.forEach(radio => {
          const rawId = radio.dataset.variantId;

          // For null-variant rows the snippet already rendered "Free"
          // server-side; nothing to do.
          if (!rawId || rawId === '' || rawId === '0') return;

          // Decide effective price. Server-side `data-price` wins, then
          // `data-price-override`, then the fetched live variant price.
          const dataPriceRaw = radio.dataset.price;
          const overrideRaw = radio.dataset.priceOverride;
          const originalPriceRaw = radio.dataset.originalPrice;

          const card = radio.closest('.delivery-options__card');
          const priceEl = card?.querySelector('[data-price-display]');
          if (!priceEl) return;

          let price = null;
          if (dataPriceRaw && dataPriceRaw !== '' && dataPriceRaw !== 'null') {
            // Server already rendered this — skip to avoid flicker.
            return;
          } else if (overrideRaw && overrideRaw !== '' && overrideRaw !== 'null') {
            price = parseInt(overrideRaw, 10);
          } else {
            const variantId = parseInt(rawId, 10);
            if (this.priceCache.has(variantId)) price = this.priceCache.get(variantId);
          }

          if (price === null || price === undefined || Number.isNaN(price)) {
            // Couldn't resolve; clear the loading dots so the card isn't
            // stuck looking unfinished.
            priceEl.querySelector('.delivery-options__price-loading')?.remove();
            return;
          }

          const originalPrice = originalPriceRaw && originalPriceRaw !== '' && originalPriceRaw !== 'null'
            ? parseInt(originalPriceRaw, 10)
            : null;

          const parts = [];
          if (Number.isFinite(originalPrice) && originalPrice > 0) {
            parts.push(
              `<span class="delivery-options__price-original" data-original-price>${this.formatMoney(originalPrice)}</span>`
            );
          }
          if (price === 0) {
            parts.push(`<span class="delivery-options__price-current delivery-options__price-current--free">Free</span>`);
          } else {
            parts.push(`<span class="delivery-options__price-current">${this.formatMoney(price)}</span>`);
          }
          priceEl.innerHTML = parts.join('');
        });
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents);
        }
        if (!this.priceFormatter) {
          const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
          this.priceFormatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
          });
        }
        return this.priceFormatter.format(cents / 100);
      }

      /**
       * Intercept the parent product-form's submit. We do this by patching
       * the form's submit flow: we pre-build an `items` array and inject it
       * into the FormData before product-form.js sends it, OR we hijack the
       * fetch entirely. We take the cleaner route: listen on the form's
       * submit event in the CAPTURE phase, prevent the default, and run our
       * own /cart/add.js call, then trigger product-form's success path.
       *
       * Implementation: rather than rewriting product-form.js, we attach a
       * capture-phase submit listener. When fired, we cancel the event,
       * issue our own multi-item add, then dispatch cart events that the
       * theme already listens for.
       */
      hookIntoProductForm() {
        if (!this.productForm) {
          console.warn('[delivery-options] no product-form found; add-on will not be added to cart.');
          return;
        }
        const form = this.productForm.querySelector('form');
        if (!form) return;

        // <delivery-options> renders OUTSIDE the <form>, so the radio inputs
        // (with name="properties[Delivery Option]") aren't picked up when
        // product-form.js builds FormData. To work around that, we keep a
        // hidden input INSIDE the form that mirrors the currently-selected
        // option's label. That hidden input rides along on the normal submit
        // and ends up as a line-item property on the main product.
        this.syncedInput = form.querySelector('input[name="properties[Delivery Option]"][data-delivery-options-sync]');
        if (!this.syncedInput) {
          this.syncedInput = document.createElement('input');
          this.syncedInput.type = 'hidden';
          this.syncedInput.name = 'properties[Delivery Option]';
          this.syncedInput.setAttribute('data-delivery-options-sync', '');
          form.appendChild(this.syncedInput);
        }
        this.updateSyncedInput();

        // Capture-phase listener so we run BEFORE product-form's bound submit.
        // Strategy: silently pre-add the fee variant to the cart, then RESUME
        // the normal product-form submit. A flag prevents an infinite loop.
        form.addEventListener('submit', this.onFormSubmit.bind(this), { capture: true });
      }

      updateSyncedInput() {
        if (!this.syncedInput) return;
        const selected = this.getSelected();
        // Always reflect the chosen label, even for free tiers — that's the
        // whole point: the customer's choice is recorded on the main product
        // so it's visible in cart, checkout, and the merchant's order detail.
        this.syncedInput.value = selected ? selected.label : '';
      }

      async onFormSubmit(event) {
        const selected = this.getSelected();

        // No fee selected, or we've already pre-added — let normal flow run.
        if (!selected || selected.variantId === null || this._feeAlreadyAdded) {
          this._feeAlreadyAdded = false; // reset for next click
          return;
        }

        // We have a fee to add. Block the normal submit, pre-add the fee
        // silently, then re-trigger the form submit. Product-form.js will
        // handle the main product, the cart drawer / notification rendering,
        // and the response shape it knows about — we don't have to.
        event.preventDefault();
        event.stopImmediatePropagation();

        const productForm = this.productForm;
        const submitButton = productForm.querySelector('[type="submit"]');
        if (submitButton?.getAttribute('aria-disabled') === 'true') return;

        // UI: keep the existing button state; product-form will manage it
        // when it runs. We just briefly indicate loading while we pre-add.
        submitButton?.setAttribute('aria-disabled', 'true');
        submitButton?.classList.add('loading');
        productForm.querySelector('.loading__spinner')?.classList.remove('hidden', 'spinning-complete');

        try {
          const formData = new FormData(productForm.querySelector('form'));
          const feeItem = this.buildFeeItem(formData, selected);

          // Silent fee add. No `sections` requested — we don't want this
          // response to drive any UI. We just want the variant in the cart.
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items: [feeItem] }),
          });
          const data = await res.json();

          if (!res.ok || data.status) {
            this.handleAddError(data, productForm);
            return;
          }

          // Pre-add succeeded. Now re-fire the form submit. Our handler will
          // see the `_feeAlreadyAdded` flag and let product-form.js take over.
          this._feeAlreadyAdded = true;

          // Reset the loading state we set above; product-form will set its
          // own as part of its submit handling.
          submitButton?.removeAttribute('aria-disabled');
          submitButton?.classList.remove('loading');
          productForm.querySelector('.loading__spinner')?.classList.add('hidden', 'spinning-complete');

          // Re-trigger submit. requestSubmit() fires the submit event so
          // product-form.js's handler runs. Some older browsers don't
          // support requestSubmit on form elements; fall back to dispatching
          // a synthetic submit event.
          const form = productForm.querySelector('form');
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitButton);
          } else {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        } catch (err) {
          console.error('[delivery-options] fee pre-add failed', err);
          submitButton?.removeAttribute('aria-disabled');
          submitButton?.classList.remove('loading');
          productForm.querySelector('.loading__spinner')?.classList.add('hidden', 'spinning-complete');
        }
      }

      buildFeeItem(formData, selected) {
        const mainQty = parseInt(formData.get('quantity') || '1', 10);
        const productId = this.dataset.productId;
        const productTitle = this.closest('product-info')?.dataset?.productTitle || '';

        return {
          id: selected.variantId,
          quantity: mainQty, // one fee per unit of the main product
          properties: {
            '_parent_product_id': productId,
            '_parent_product_title': productTitle,
            'For': productTitle || `Product #${productId}`,
          },
        };
      }

      handleAddError(response, productForm) {
        const submitButton = productForm.querySelector('[type="submit"]');
        const errorMsg = response.description || response.message || 'Could not add item to cart.';

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartError, {
            source: 'delivery-options',
            errors: response.errors || response.description,
            message: response.message,
          });
        }

        // Show the existing product-form error wrapper if available
        const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
        const messageEl = wrapper?.querySelector('.product-form__error-message');
        if (wrapper && messageEl) {
          messageEl.textContent = errorMsg;
          wrapper.removeAttribute('hidden');
        } else {
          alert(errorMsg);
        }

        submitButton?.removeAttribute('aria-disabled');
        submitButton?.classList.remove('loading');
        productForm.querySelector('.loading__spinner')?.classList.add('hidden', 'spinning-complete');
      }
    }
  );
}